import { Component, signal } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { L10nTranslationService } from 'angular-l10n';

@Component({
  selector: 'miq-time-period-validation-cell-renderer',
  styleUrls: ['./time-period-validation-renderer.component.scss'],
  template: `
    <div class="time-period-cell">
      <div class="time-period-content">
        <span class="time-period-text">{{ value }}</span>
        <span *ngIf="subLabel" class="time-period-sub-label">{{ subLabel }}</span>
      </div>
      <mat-icon
        *ngIf="iconName"
        class="validation-icon"
        [class.error-icon]="isError()"
        [class.warning-icon]="!isError()"
        [matTooltip]="tooltipMessage"
        matTooltipPosition="right"
        matTooltipClass="validation-tooltip"
      >
        {{ iconName }}
      </mat-icon>
    </div>
  `,
  standalone: false,
})
export class TimePeriodValidationCellRendererComponent implements ICellRendererAngularComp {
  public value: string;
  public subLabel: string | null = null;
  public iconName = '';
  public isError = signal<boolean>(false);
  public tooltipMessage = '';

  constructor(private translationService: L10nTranslationService) {}

  agInit(params: ICellRendererParams): void {
    this.value = params.value;
    this.subLabel = params.node?.data?.currentPeriodSubLabel ?? null;
    this.updateValidationState(params);
  }

  setValidationIcon(iconName = '', isError = false, tooltipMessage = ''): void {
    this.iconName = iconName;
    this.isError.set(isError);
    this.tooltipMessage = tooltipMessage;
  }

  refresh(params: ICellRendererParams): boolean {
    this.value = params.value;
    this.subLabel = params.node?.data?.currentPeriodSubLabel ?? null;
    this.setValidationIcon();
    this.updateValidationState(params);
    return true;
  }

  private updateValidationState(params: ICellRendererParams): void {
    const data = params.node?.data;

    if (!data) {
      this.setValidationIcon();
      return;
    }

    // Check validation directly on the data
    const hasHardError = this.checkForHardErrors(data, params.context);
    const hasSoftWarning = !hasHardError && this.checkForSoftWarnings(data, params.context);

    if (hasHardError) {
      this.setValidationIcon('warning', true, this.getValidationMessages(data, true, params.context));
    } else if (hasSoftWarning) {
      this.setValidationIcon('warning', false, this.getValidationMessages(data, false, params.context));
    } else {
      this.setValidationIcon();
    }
  }

  private checkForHardErrors(data: any, context: any): boolean {
    const hasValue = (value: any) => value != null && value !== 0 && value !== '';

    // Check Direct Weighted Area
    const directWeightedArea = data?.yearly_direct_lease_rate_denominator;
    const directAvailableArea = data?.totalDirectAvailableSurfaceArea;
    const directRate =
      context?.getRentDisplay?.() === 'Monthly'
        ? data?.averageMonthlyDirectLeaseRate
        : data?.averageYearlyDirectLeaseRate;

    if (hasValue(directWeightedArea) && hasValue(directAvailableArea) && directWeightedArea > directAvailableArea) {
      // Rule 1: weighted area greater than available area
      return true;
    }

    if (hasValue(directWeightedArea)) {
      const isRateEmpty = !hasValue(directRate);
      const isAreaEmpty = !hasValue(directAvailableArea);

      if (isRateEmpty || isAreaEmpty) {
        // Rule 2: weighted area populated but rate or area is empty
        return true;
      }
    }

    // Check Sublease Weighted Area
    const subleaseWeightedArea = data?.yearlySubLeaseRateDenominator;
    const subleaseAvailableArea = data?.totalAvailableSubleaseArea;
    const subleaseRate =
      context?.getRentDisplay?.() === 'Monthly' ? data?.averageMonthlySubleaseRate : data?.averageYearlySubleaseRate;

    if (
      hasValue(subleaseWeightedArea) &&
      hasValue(subleaseAvailableArea) &&
      subleaseWeightedArea > subleaseAvailableArea
    ) {
      // Rule 1: weighted area greater than available area
      return true;
    }

    if (hasValue(subleaseWeightedArea)) {
      const isRateEmpty = !hasValue(subleaseRate);
      const isAreaEmpty = !hasValue(subleaseAvailableArea);

      if (isRateEmpty || isAreaEmpty) {
        // Rule 2: weighted area populated but rate or area is empty
        return true;
      }
    }

    return false;
  }

  private checkForSoftWarnings(data: any, context: any): boolean {
    const hasValue = (value: any) => value != null && value !== 0 && value !== '';

    // Check Direct out-of-sync
    const directWeightedArea = data?.yearly_direct_lease_rate_denominator;
    const directAvailableArea = data?.totalDirectAvailableSurfaceArea;
    const directRate =
      context?.getRentDisplay?.() === 'Monthly'
        ? data?.averageMonthlyDirectLeaseRate
        : data?.averageYearlyDirectLeaseRate;

    if (
      hasValue(directRate) &&
      hasValue(directAvailableArea) &&
      hasValue(directWeightedArea) &&
      directWeightedArea !== directAvailableArea &&
      directWeightedArea <= directAvailableArea
    ) {
      return true;
    }

    // Check Sublease out-of-sync
    const subleaseWeightedArea = data?.yearlySubLeaseRateDenominator;
    const subleaseAvailableArea = data?.totalAvailableSubleaseArea;
    const subleaseRate =
      context?.getRentDisplay?.() === 'Monthly' ? data?.averageMonthlySubleaseRate : data?.averageYearlySubleaseRate;

    if (
      hasValue(subleaseRate) &&
      hasValue(subleaseAvailableArea) &&
      hasValue(subleaseWeightedArea) &&
      subleaseWeightedArea !== subleaseAvailableArea &&
      subleaseWeightedArea <= subleaseAvailableArea
    ) {
      return true;
    }

    return false;
  }

  private getValidationMessages(data: any, isError: boolean, context: any): string {
    const messages: string[] = [];
    const hasValue = (value: any) => value != null && value !== 0 && value !== '';

    // Check Direct Weighted Area
    const directWeightedArea = data?.yearly_direct_lease_rate_denominator;
    const directAvailableArea = data?.totalDirectAvailableSurfaceArea;
    const directRate =
      context?.getRentDisplay?.() === 'Monthly'
        ? data?.averageMonthlyDirectLeaseRate
        : data?.averageYearlyDirectLeaseRate;

    if (hasValue(directWeightedArea)) {
      const isRateEmpty = !hasValue(directRate);
      const isAreaEmpty = !hasValue(directAvailableArea);

      if (isError) {
        if (hasValue(directAvailableArea) && directWeightedArea > directAvailableArea) {
          // Rule 1 message
          messages.push(
            this.translationService.translate('detailsErrors.historicalStats.weightedAreaGreaterThanAvailable', {
              type: 'Direct',
            })
          );
        } else if (isRateEmpty || isAreaEmpty) {
          // Rule 2 message
          messages.push(
            this.translationService.translate('detailsErrors.historicalStats.weightedAreaShouldBeNullOrZero', {
              type: 'Direct',
            })
          );
        }
      } else if (
        hasValue(directRate) &&
        hasValue(directAvailableArea) &&
        directWeightedArea !== directAvailableArea &&
        directWeightedArea <= directAvailableArea
      ) {
        // Rule 3 message (soft warning)
        messages.push(
          this.translationService.translate('detailsErrors.historicalStats.weightedAreaOutOfSync', {
            type: 'Direct',
          })
        );
      }
    }

    // Check Sublease Weighted Area
    const subleaseWeightedArea = data?.yearlySubLeaseRateDenominator;
    const subleaseAvailableArea = data?.totalAvailableSubleaseArea;
    const subleaseRate =
      context?.getRentDisplay?.() === 'Monthly' ? data?.averageMonthlySubleaseRate : data?.averageYearlySubleaseRate;

    if (hasValue(subleaseWeightedArea)) {
      const isRateEmpty = !hasValue(subleaseRate);
      const isAreaEmpty = !hasValue(subleaseAvailableArea);

      if (isError) {
        if (hasValue(subleaseAvailableArea) && subleaseWeightedArea > subleaseAvailableArea) {
          // Rule 1 message
          messages.push(
            this.translationService.translate('detailsErrors.historicalStats.weightedAreaGreaterThanAvailable', {
              type: 'Sublease',
            })
          );
        } else if (isRateEmpty || isAreaEmpty) {
          // Rule 2 message
          messages.push(
            this.translationService.translate('detailsErrors.historicalStats.weightedAreaShouldBeNullOrZero', {
              type: 'Sublease',
            })
          );
        }
      } else if (
        hasValue(subleaseRate) &&
        hasValue(subleaseAvailableArea) &&
        subleaseWeightedArea !== subleaseAvailableArea &&
        subleaseWeightedArea <= subleaseAvailableArea
      ) {
        // Rule 3 message (soft warning)
        messages.push(
          this.translationService.translate('detailsErrors.historicalStats.weightedAreaOutOfSync', {
            type: 'Sublease',
          })
        );
      }
    }

    return messages.join('\n\n');
  }
}
